import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Fade,
  Skeleton,
  Badge
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import KeywordIcon from '@mui/icons-material/Label';
import ArticleIcon from '@mui/icons-material/Article';
import RefreshIcon from '@mui/icons-material/Refresh';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';

import Api from '../../Services/Api';

const KeywordSuggestions = ({ highPerformingTerms = [] }) => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Generate suggestions when high performing terms change
  useEffect(() => {
    if (highPerformingTerms.length > 0) {
      generateSuggestions();
    } else {
      setSuggestions(null);
      setShowSuggestions(false);
    }
  }, [highPerformingTerms]);

  const generateSuggestions = async () => {
    if (highPerformingTerms.length === 0) {
      setError('No high-performing terms available to generate suggestions');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log('Generating keyword suggestions for:', highPerformingTerms);
      
      const response = await Api.generateKeywordSuggestions(highPerformingTerms);
      
      console.log('Keyword suggestions response:', response);
      
      if (response && response.success && response.data) {
        setSuggestions(response.data);
        setShowSuggestions(true);
        
        // Debug logging for the new structure
        console.log('AI suggestions with novelty detection:', {
          keywords: response.data.related_keywords,
          titles: response.data.article_titles,
          stats: response.data.stats
        });
      } else {
        setError('Failed to generate keyword suggestions');
      }
    } catch (err) {
      console.error('Error generating keyword suggestions:', err);
      setError('An error occurred while generating keyword suggestions');
    } finally {
      setLoading(false);
    }
  };

  // Generate keywords using OpenAI for better relevance
  const generateKeywordsWithAI = async (title, context = 'guide') => {
    try {
      // Use the Api class method instead of direct fetch
      const response = await Api.generateKeywordsForTitle(title, highPerformingTerms, context);

      if (response && response.success && response.keywords) {
        return response.keywords;
      }
      
      // Fallback to manual generation if API fails
      return generateKeywordsFallback(title, context);
      
    } catch (error) {
      console.error('Error generating keywords with AI:', error);
      return generateKeywordsFallback(title, context);
    }
  };

  // Fallback keyword generation (original logic)
  const generateKeywordsFallback = (title, context) => {
    const titleWords = title.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(' ')
      .filter(word => word.length > 2)
      .filter(word => !['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had', 'her', 'was', 'one', 'our', 'out', 'day', 'had', 'his', 'how', 'its', 'may', 'new', 'now', 'old', 'see', 'two', 'who', 'boy', 'did', 'has', 'let', 'put', 'say', 'she', 'too', 'use'].includes(word));

    const relatedKeywords = [];
    
    // Add main topic words
    const importantWords = titleWords.filter(word => 
      !['guide', 'complete', 'best', 'how', 'tips', 'review', 'ultimate', 'top'].includes(word)
    );
    
    importantWords.slice(0, 2).forEach(word => {
      if (!relatedKeywords.includes(word)) {
        relatedKeywords.push(word);
      }
    });
    
    // Add context word
    if (context && !relatedKeywords.includes(context)) {
      relatedKeywords.push(context);
    }
    
    // Fill remaining with fallback terms
    const fallbackTerms = ['best', 'tips', 'complete'];
    fallbackTerms.forEach(term => {
      if (relatedKeywords.length < 3 && !relatedKeywords.includes(term)) {
        relatedKeywords.push(term);
      }
    });
    
    return relatedKeywords.slice(0, 3);
  };

  const handleGenerateContent = async (keywordData) => {
    // Extract keyword string from the object or use directly if it's a string
    const keyword = typeof keywordData === 'object' ? keywordData.keyword : keywordData;
    
    // The title will be "Complete Guide to ${keyword}"
    const title = `Complete Guide to ${keyword}`;
    
    // Show loading state
    setLoading(true);
    
    try {
      // Generate keywords using AI
      const aiKeywords = await generateKeywordsWithAI(title, 'guide');
      
      // Navigate to generator with AI-generated keywords (limit to 5 for API compliance)
      navigate('/rsoc/generator', { 
        state: { 
          prefilledKeywords: Array.isArray(aiKeywords) ? aiKeywords.slice(0, 5) : aiKeywords,
          prefilledTitle: title
        }
      });
    } catch (error) {
      console.error('Error generating keywords:', error);
      // Fallback to original method
      const fallbackKeywords = generateKeywordsFallback(title, 'guide');
      navigate('/rsoc/generator', { 
        state: { 
          prefilledKeywords: fallbackKeywords,
          prefilledTitle: title
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFromTitle = async (titleData) => {
    // Extract title string from the object or use directly if it's a string
    const title = typeof titleData === 'object' ? titleData.title : titleData;
    
    // Show loading state
    setLoading(true);
    
    try {
      // Generate keywords using AI based on the title
      const aiKeywords = await generateKeywordsWithAI(title, 'content');
      
      navigate('/rsoc/generator', { 
        state: { 
          prefilledTitle: title,
          prefilledKeywords: Array.isArray(aiKeywords) ? aiKeywords.slice(0, 5) : aiKeywords
        }
      });
    } catch (error) {
      console.error('Error generating keywords:', error);
      // Fallback to original method
      const fallbackKeywords = generateKeywordsFallback(title, 'content');
      navigate('/rsoc/generator', { 
        state: { 
          prefilledTitle: title,
          prefilledKeywords: fallbackKeywords
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Don't render if no high performing terms
  if (highPerformingTerms.length === 0) {
    return (
      <Paper sx={{ p: 3, mt: 3, borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AutoAwesomeIcon sx={{ mr: 1, color: 'action.disabled' }} />
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            AI-Generated Content Opportunities
          </Typography>
        </Box>
        <Alert severity="info">
          <Typography variant="body2">
            No high-performing terms (EPC ≥ $0.80) found in current results. 
            Try adjusting your filters to find high-value terms for AI-powered content suggestions.
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 3, borderRadius: 2, boxShadow: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AutoAwesomeIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 500, flexGrow: 1 }}>
          AI-Generated Content Opportunities
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={generateSuggestions}
          disabled={loading || highPerformingTerms.length === 0}
          size="small"
        >
          Regenerate
        </Button>
      </Box>

      {/* High Performing Terms Display */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
          Based on {highPerformingTerms.length} high-performing term{highPerformingTerms.length > 1 ? 's' : ''}:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {highPerformingTerms.slice(0, 5).map((term, index) => (
            <Chip 
              key={index} 
              label={term} 
              size="small" 
              color="success" 
              variant="outlined"
            />
          ))}
          {highPerformingTerms.length > 5 && (
            <Chip 
              label={`+${highPerformingTerms.length - 5} more`} 
              size="small" 
              color="default" 
              variant="outlined"
            />
          )}
        </Box>
        
        {/* Stats display when suggestions are available */}
        {suggestions && suggestions.stats && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
              <strong>AI-Powered Similarity Analysis:</strong> ChatGPT analyzed {suggestions.stats.total_existing_keywords} existing keywords and titles from your RSOC pages using ultra-conservative similarity matching. Similar suggestions show which existing content they relate to.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                icon={<NewReleasesIcon />}
                label={`${suggestions.stats.new_keywords_count} new keywords`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip 
                icon={<NewReleasesIcon />}
                label={`${suggestions.stats.new_titles_count} new titles`}
                size="small"
                color="secondary"
                variant="outlined"
              />
              <Chip 
                icon={<AutoAwesomeIcon />}
                label={`Ultra-conservative matching`}
                size="small"
                color="success"
                variant="outlined"
              />
            </Box>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography variant="body2" color="textSecondary">
            Generating keyword suggestions with AI...
          </Typography>
        </Box>
      )}

      {showSuggestions && suggestions && !loading && (
        <Fade in={showSuggestions}>
          <Grid container spacing={3}>
            {/* Related Keywords */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <KeywordIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Related Keywords
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                    High-value commercial keywords for content creation
                  </Typography>
                  
                  {suggestions.related_keywords && suggestions.related_keywords.length > 0 ? (
                    <List dense>
                      {suggestions.related_keywords.map((keywordData, index) => {
                        const keyword = keywordData.keyword || keywordData; // Handle both old and new formats
                        const isNew = keywordData.is_new !== undefined ? keywordData.is_new : true; // Default to new if not specified
                        const relatedTo = keywordData.related_to || null;
                        
                        return (
                          <ListItem 
                            key={index} 
                            sx={{ 
                              px: 0, 
                              py: 0.5,
                              '&:hover': { 
                                backgroundColor: 'action.hover',
                                borderRadius: 1,
                                cursor: 'pointer'
                              }
                            }}
                            onClick={() => handleGenerateContent(keywordData)}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <KeywordIcon fontSize="small" color="action" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">
                                      {keyword}
                                    </Typography>
                                    {isNew && (
                                      <Chip 
                                        label="NEW" 
                                        size="small" 
                                        color="primary" 
                                        sx={{ 
                                          height: 20, 
                                          fontSize: '0.6rem',
                                          fontWeight: 'bold'
                                        }} 
                                      />
                                    )}
                                  </Box>
                                  {!isNew && relatedTo && (
                                    <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                      Similar to: {relatedTo}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No keywords generated
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Article Titles */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ArticleIcon sx={{ mr: 1, color: 'warning.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Article Titles
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                    SEO-optimized titles for high-performing content
                  </Typography>
                  
                  {suggestions.article_titles && suggestions.article_titles.length > 0 ? (
                    <List dense>
                      {suggestions.article_titles.map((titleData, index) => {
                        const title = titleData.title || titleData; // Handle both old and new formats
                        const isNew = titleData.is_new !== undefined ? titleData.is_new : true; // Default to new if not specified
                        const relatedTo = titleData.related_to || null;
                        
                        return (
                          <ListItem 
                            key={index} 
                            sx={{ 
                              px: 0, 
                              py: 0.5,
                              '&:hover': { 
                                backgroundColor: 'action.hover',
                                borderRadius: 1,
                                cursor: 'pointer'
                              }
                            }}
                            onClick={() => handleGenerateFromTitle(titleData)}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <ArticleIcon fontSize="small" color="action" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">
                                      {title}
                                    </Typography>
                                    {isNew && (
                                      <Chip 
                                        label="NEW" 
                                        size="small" 
                                        color="secondary" 
                                        sx={{ 
                                          height: 20, 
                                          fontSize: '0.6rem',
                                          fontWeight: 'bold'
                                        }} 
                                      />
                                    )}
                                  </Box>
                                  {!isNew && relatedTo && (
                                    <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                      Similar to: {relatedTo}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No article titles generated
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Fade>
      )}

      {/* Loading skeletons */}
      {loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
                {[...Array(5)].map((_, index) => (
                  <Skeleton key={index} variant="text" width="80%" height={24} sx={{ mb: 1 }} />
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
                {[...Array(5)].map((_, index) => (
                  <Skeleton key={index} variant="text" width="90%" height={24} sx={{ mb: 1 }} />
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default KeywordSuggestions; 